import { cn } from "@-/com.npmjs.cn";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useHasHydrated } from "@-/dev.improve.haniverse.lib.hydration";
import { Pressable, ScrollView, useWindowDimensions, View } from "@-/com.npmjs.react-native";
import type { ReactNode } from "react";

/** Below this window width an open sidebar would crowd the page, so it starts collapsed. */
const SIDEBAR_OPEN_MIN_WINDOW_WIDTH = 768;

export interface WebSectionSidebarItem {
  key: string;
  label: string;
  onPress: () => void;
  selected: boolean;
  testID?: string;
}

/**
 * Web's sub-section navigation: the page's sections as tabs stacked in a left sidebar, instead of
 * a second tab row under the site header.
 *
 * The sidebar always collapses: its toggle folds it to a slim rail holding only the toggle, and
 * the choice persists on the device (`isWebSectionSidebarCollapsed`), shared by every section
 * sidebar. Until the viewer chooses, narrow windows start collapsed.
 *
 * `items` is `null` where the page has no sections. The page keeps its place in the tree either
 * way, so the sidebar can appear or disappear without remounting it.
 */
export function WebSectionSidebarLayout({
  accessibilityLabel,
  children,
  items,
}: {
  accessibilityLabel: string;
  children: ReactNode;
  items: readonly WebSectionSidebarItem[] | null;
}) {
  const { width } = useWindowDimensions();
  const [collapsedPreference, setCollapsedPreference] = useDeviceSetting(
    "isWebSectionSidebarCollapsed",
  );
  // The preference and the window width are browser-only: the server and the hydrating render
  // show the sidebar open, then it settles.
  const hasHydrated = useHasHydrated();
  const isCollapsed =
    hasHydrated && (collapsedPreference ?? width < SIDEBAR_OPEN_MIN_WINDOW_WIDTH);

  return (
    <View className="flex-1 flex-row">
      {items === null ? null : (
        // RNW gives a scroll view `flex-grow: 1` that its classes cannot override, so the sidebar
        // takes its size from this wrapper and the scroll view fills it.
        <View
          className={cn(
            "shrink-0 border-r border-border bg-background",
            isCollapsed ? "w-12" : "w-56",
          )}
          testID="web-section-sidebar"
        >
          <View className={cn("flex-row px-2.25 pt-2.25", isCollapsed ? "justify-center" : "")}>
            <Pressable
              accessibilityLabel={isCollapsed ? `Show ${accessibilityLabel}` : `Hide ${accessibilityLabel}`}
              accessibilityRole="button"
              accessibilityState={{ expanded: !isCollapsed }}
              className="size-8 items-center justify-center rounded-xs hover:bg-accent active:bg-accent"
              hitSlop={4}
              onPress={() => {
                setCollapsedPreference(!isCollapsed);
              }}
              testID="web-section-sidebar-toggle"
            >
              <Icon
                className="text-muted-foreground"
                name={isCollapsed ? "TbLayoutSidebar" : "TbLayoutSidebarFilled"}
                size={20}
              />
            </Pressable>
          </View>
          {isCollapsed ? null : (
            <ScrollView
              accessibilityLabel={accessibilityLabel}
              accessibilityRole="tablist"
              contentContainerClassName="gap-0.5 p-2.25"
              showsVerticalScrollIndicator={false}
            >
              {items.map((item) => (
                <Pressable
                  accessibilityRole="tab"
                  accessibilityState={{ selected: item.selected }}
                  className={cn(
                    "min-h-10 flex-row items-center rounded-xs px-3 py-2 hover:bg-accent active:bg-accent",
                    item.selected && "bg-accent",
                  )}
                  key={item.key}
                  onPress={item.onPress}
                  testID={item.testID}
                >
                  <Text
                    className={cn(
                      "text-sm",
                      item.selected
                        ? "font-semibold text-foreground"
                        : "font-medium text-muted-foreground",
                    )}
                    numberOfLines={1}
                  >
                    {item.label}
                  </Text>
                </Pressable>
              ))}
            </ScrollView>
          )}
        </View>
      )}
      <View className="min-w-0 flex-1">{children}</View>
    </View>
  );
}
